import { Chart, FontSpec } from 'chart.js';
import { Area, Font } from '@/features/reports/components/chartjs/plugins/doughnutLabels/types';

// Utility to handle null/undefined checks (replacement for helpers.isNullOrUndef)
const isNullOrUndef = (value: any): boolean => value === null || value === undefined;

// Utility to get a default value (replacement for helpers.valueOrDefault)
const valueOrDefault = <T>(value: T | undefined | null, defaultValue: T): T =>
  isNullOrUndef(value) ? defaultValue : (value as T);

// Utility to convert lineHeight (replacement for helpers.options.toLineHeight)
const toLineHeight = (lineHeight: number | string | undefined, size: number): number => {
  if (isNullOrUndef(lineHeight)) return size * 1.2; // Default line height factor
  if (typeof lineHeight === 'number') return lineHeight;
  const parsed = parseFloat(lineHeight as string);
  return isNaN(parsed) ? size * 1.2 : parsed;
};

const utils = {
  parseFont: (value: Partial<FontSpec>): Font => {
    const defaults = Chart.defaults.font; // Chart.js 4 defaults.font
    const size = valueOrDefault(value.size, defaults.size || 13); // Default size from Chart.js
    const font: Font = {
      family: valueOrDefault(value.family, defaults.family || 'Arial'),
      lineHeight: toLineHeight(value.lineHeight, size),
      size,
      style: valueOrDefault(value.style, defaults.style || 'normal'),
      weight: valueOrDefault(value.weight, defaults.weight ? defaults.weight : 'normal'),
      string: '',
    };

    font.string = utils.toFontString(font);
    return font;
  },

  toFontString: (font: Font): string => {
    if (!font || isNullOrUndef(font.size) || isNullOrUndef(font.family)) {
      return '';
    }

    return `${font.style || ''} ${font.weight || ''} ${font.size}px ${font.family}`.trim();
  },

  textSize: (ctx: CanvasRenderingContext2D, labels: { text: string; font: Font }[]): Area => {
    const prev = ctx.font;
    let width = 0;
    let height = 0;

    for (const item of labels) {
      ctx.font = item.font.string || '13px Arial';
      const measuredText = ctx.measureText(item.text);
      width = Math.max(measuredText.width, width);
      height += item.font.lineHeight || 24; // Fallback to 24 if lineHeight is undefined
    }

    ctx.font = prev;
    return { height, width };
  },
};

export default utils;
